import { BaseFormComponent } from 'src/app/@shared/form/base-form.component';
import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  DetailListItem,
  DetailListSubItem, SubItemEntry,
} from 'src/app/@shared/detail/detail-list-item/detail-list-item.component';
import { FormConfig, FormFieldKey, FormFieldsetKey } from 'src/app/@shared/form/form.types';

import { ClusterModel } from 'src/app/cluster/@shared/cluster.model';
import { DETAIL_FIELDSET_CONFIG, DetailFieldsetConfig, DetailFieldsetKey } from 'src/app/@shared/detail/detail-fieldset-config';
import {
  DetailContentKey,
  HIDDEN_FIELDS,
  HIDDEN_FIELDS_BY_TYPE,
  HIDDEN_FIELDS_ON_PUBLIC_ACCESS,
} from 'src/app/@shared/detail/detail-config';
import { DynamicFormFieldModel } from 'src/app/@shared/form/dynamic-form-field/dynamic-form-field.model';
import { FormFieldModel } from 'src/app/@shared/form/form-field/form-field.model';
import { FormKey, ORGANIZATION_CONTACT_FORM_CONFIG } from 'src/app/@shared/form/form-config';
import { OrganizationModel } from 'src/app/organization/@shared/organization.model';
import { PUBLIC_CO_ORGANIZATION_RELATION_FIELDS } from '@api/organization/@shared/public-fields.helper';
import { PersonModel } from 'src/app/person/@shared/person.model';
import { TopicModel } from 'src/app/topic/@shared/topic.model';
import { UserModel } from 'src/app/user/@shared/user.model';
import { applyDetailIconGroups } from 'src/app/@shared/detail/detail-icon-helper';
import { createFieldsByConfig, getFormKey } from 'src/app/@shared/form/form-helper';
import { getFormattedEntries } from 'src/app/@shared/form/form-value-formatter';

type DatasetData = OrganizationModel | PersonModel | TopicModel | ClusterModel | UserModel;

type DetailList = {
  id: DetailFieldsetKey;
  items: DetailListItem[];
  title?: string;
};

@Component({
  selector: 'detail-content',
  standalone: false,
  styleUrls: ['./detail-content.component.scss'],
  templateUrl: './detail-content.component.html',
})
export class DetailContentComponent extends BaseFormComponent<DatasetData> implements OnChanges {
  @Input() public datasetType: DetailContentKey;
  @Input() public isOrganization: boolean;
  @Input() public isInitialized: boolean;
  @Input() public isUnauthorized: boolean = false;
  @Input() public showOrganizationCoInformation: boolean = false;
  @Input() public hiddenFieldsets: (FormFieldKey | FormFieldsetKey | DetailFieldsetKey)[] = [];

  public detailContent: {
    lists: DetailList[];
  } = {
      lists: [],
    };

  private organizationContactFields = createFieldsByConfig(ORGANIZATION_CONTACT_FORM_CONFIG);

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.datasetData && (changes.isUnauthorized || changes.showOrganizationCoInformation || changes.hiddenFieldsets)) {
      this.detailContent = this.getMappedContent(this.datasetData);
    }
  }

  protected getFormId(): FormKey {
    if (this.isOrganization) {
      const dataset = this.datasetData as OrganizationModel;

      return getFormKey(dataset.type, dataset.subType, dataset.subTypeForm);
    }

    return getFormKey(this.datasetData.type);
  }

  protected onDataReceived(data: any): void {
    this.datasetData = {
      ...data,
    };

    this.prepareForm();

    if (!this.isInitialized) {
      this.detailContent = this.getMappedContent(this.datasetData);

      return;
    }

    // after we update our data via edit form, we clear everything first
    // to ensure our detail view shows the current data
    this.detailContent.lists = [];

    requestAnimationFrame(() => {
      this.detailContent = this.getMappedContent(this.datasetData);
    });
  }

  protected setConfig(config: FormConfig): void {
    this.allFieldConfigs = createFieldsByConfig(config);
  }

  private getMappedContent(data: DatasetData): { lists: DetailList[] } {
    const mappedLists: DetailList[] = [];

    for (const fieldset of DETAIL_FIELDSET_CONFIG) {
      if (this.hiddenFieldsets.includes(fieldset.id)) {
        continue;
      }

      const items = fieldset.id === 'organization-co-information'
        ? this.getOrganizationCoItems(data as OrganizationModel)
        : this.getDetailItems(this.getDetailFields(fieldset), data);

      if (items.length) {
        applyDetailIconGroups(items, fieldset.id);
        mappedLists.push({
          id: fieldset.id,
          items,
          title: fieldset.title,
        });
      }
    }

    return {
      lists: mappedLists,
    };
  }

  private getOrganizationCoItems(data: OrganizationModel): DetailListItem[] {
    if (!this.isOrganization || !this.showOrganizationCoInformation || !this.isUnauthorized) {
      return [];
    }

    const organization = data.localContactPoint || data.contactOrganization;

    if (!organization) {
      return [];
    }

    const fields = PUBLIC_CO_ORGANIZATION_RELATION_FIELDS.map((fieldId) => {
      return this.organizationContactFields.find((field) => { return field.id === fieldId; });
    }).filter((field) => { return !!field; });

    return this.getDetailItems(fields, organization);
  }

  private getDetailItems(fields: FormFieldModel[], data: Partial<DatasetData>): DetailListItem[] {
    return fields.flatMap((field) => {
      return field.type === 'dynamic'
        ? this.getDynamicItems(field as DynamicFormFieldModel, data?.[field.id])
        : this.getSimpleItems([field], data);
    }).filter((item) => { return !!item; });
  }

  private getDetailFields(fieldset: DetailFieldsetConfig): FormFieldModel[] {
    if (fieldset.fieldIds) {
      return fieldset.fieldIds.map((fieldId) => {
        return this.allFieldConfigs.find((field) => { return field.id === fieldId; });
      }).filter((field) => { return !!field; });
    }

    const assignedFieldIds = new Set(DETAIL_FIELDSET_CONFIG.flatMap((config) => {
      return config.fieldIds || [];
    }));

    return this.allFieldConfigs.filter((field) => {
      return !assignedFieldIds.has(field.id);
    });
  }

  private getDynamicItems(item: DynamicFormFieldModel,
    values: { [key in FormFieldKey]?: any }[]): DetailListItem[] {
    if (!Array.isArray(values) || this.fieldIsHidden(item)) {
      return [];
    }

    // Flat fields have one sub item with all display values.
    if (item.valueType === 'flatArray' || item.id === 'emailsExtended' || item.id === 'phoneContacts'
      || item.id === 'orphanetSynonyms') {
      const contactEntries = item.id === 'emailsExtended' || item.id === 'phoneContacts'
        ? this.getFlatContactEntries(item, values)
        : null;
      const flatValues = contactEntries
        ? contactEntries.map((entry) => { return entry.value; })
        : item.id === 'orphanetSynonyms'
          ? values.map((value) => { return value?.orphanetSynonym; }).filter((value) => { return !!value?.trim(); })
          : values;
      const flat = this.getMappedItem(item, [item], {
        [item.id]: flatValues,
      }, 0);

      if (flat) {
        flat.valueType = 'flatArray';
        flat.subItems[0].valueType = 'flatArray';
        flat.subItems[0].entries = contactEntries || flat.subItems[0].entries;
        flat.subItems[0].label = item.listLabel;
      }

      return flat
        ? [flat]
        : [];
    }

    const { fields: subFields } = (item as DynamicFormFieldModel).dynamicFieldConfig;
    const visibleItems = (subFields || []).filter((field) => {
      return !this.fieldIsHidden(field);
    });

    if (!visibleItems?.length) {
      return [];
    }

    return values.map((value, index) => {
      return this.getMappedItem(item, visibleItems, value, index);
    });
  }

  private getFlatContactEntries(item: DynamicFormFieldModel,
    values: { [key in FormFieldKey]?: string | boolean }[]): SubItemEntry[] {
    const visibleFields = item.dynamicFieldConfig.fields.filter((field) => {
      return !this.fieldIsHidden(field);
    });
    const isEmail = item.id === 'emailsExtended';
    const valueFieldId = isEmail
      ? 'email'
      : 'number';
    const extraFieldId = isEmail
      ? 'useForDataReview'
      : 'officeHours';
    const valueField = visibleFields.find((field) => { return field.id === valueFieldId; });
    const extraField = visibleFields.find((field) => {
      return field.id === extraFieldId;
    });

    if (!valueField) {
      return [];
    }

    return values.filter((value) => { return !!value?.[valueField.id]?.toString().trim(); }).map((value, index) => {
      const entry: SubItemEntry = {
        id: `${item.id}_${index}`,
        value: value[valueField.id].toString(),
      };

      if (isEmail) {
        entry.href = `mailto:${entry.value}`;

        if (extraField && value[extraField.id] === true) {
          entry.suffix = this.translate.instant(extraField.label);
        }
      } else if (extraField && value[extraField.id]?.toString().trim()) {
        const label = this.translate.instant(extraField.label);

        entry.suffix = `${label}: ${value[extraField.id]}`;
      }

      return entry;
    });
  }

  private getSimpleItems(items: FormFieldModel[],
    values: { [key in FormFieldKey]?: any }): DetailListItem[] {
    if (!Array.isArray(items)) {
      return [];
    }

    const visibleItems = (items || []).filter((field) => {
      return !this.fieldIsHidden(field);
    });

    if (!visibleItems?.length) {
      return [];
    }

    return visibleItems.map((item) => {
      return this.getMappedItem(item, [item], {
        [item.id]: values?.[item.id],
      }, 0);
    })
      .filter((item) => {
        return !!item;
      });
  }

  // if field = FormFieldModel (simple), subFields = [field]
  private getMappedItem(field: DynamicFormFieldModel | FormFieldModel,
    subFields: FormFieldModel[],
    value: any,
    index: number): DetailListItem | null {
    const subItems = this.getSubItems(subFields, value);

    if (!subItems.length) {
      return null;
    }

    return {
      id: `${field.id}_${index}`, // we need to avoid duplicated ids here
      inputType: field.inputType,
      key: field.id,
      label: field.label,
      subItems,
      type: field.type,
      valueType: field.valueType,
    };
  }

  private getSubItems(fields: FormFieldModel[],
    value: any): DetailListSubItem[] {
    if (!fields?.length) {
      return [];
    }

    if (!value) {
      return [];
    }

    return fields.map((field) => {
      let entries: SubItemEntry[];
      const rawFieldValue = value[field.id];
      let normalizedFieldValue = rawFieldValue;

      if (field.id === 'person' && !rawFieldValue && value.name) {
        normalizedFieldValue = {
          name: value.name,
        };
      }

      try {
        entries = getFormattedEntries(field, normalizedFieldValue).filter((entry) => {
          return !!entry.value.trim();
        });
      } catch (error) {
        console.error(field.id, normalizedFieldValue, error?.message);
      }

      // hide fields which are empty
      if (!entries?.length) {
        return null;
      }

      return {
        entries,
        id: field.id,
        inputType: field.inputType,
        label: field.label,
        type: field.type,
        valueType: field.valueType,
      };
    }).filter((item) => { return !!item; });
  }

  private fieldIsHidden(field: DynamicFormFieldModel | FormFieldModel): boolean {
    return (field.inputType === 'hidden' || field.isHidden)
      || HIDDEN_FIELDS.includes(field.id)
      || (this.isUnauthorized && HIDDEN_FIELDS_ON_PUBLIC_ACCESS.includes(field.id))
      || HIDDEN_FIELDS_BY_TYPE[this.datasetType]?.includes(field.id);
  }
}
